import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Badge } from '@/components/atoms/badge';
import { Progress } from '@/components/atoms/progress';
import { Separator } from '@/components/atoms/separator';
import { useCompany } from '../hooks/use-company';
import {
  Building2,
  MapPin,
  Store,
  Users,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Crown,
  Package,
  BarChart3,
  Megaphone,
  Scale,
  Gift,
  Calendar,
  Hash,
  User,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export function CompanyContainer() {
  const { t } = useTranslation();
  const { data: company, isLoading, error } = useCompany();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('settings.company.error')}</p>
      </div>
    );
  }

  if (!company) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4 text-center">
            <Building2 className="size-12 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-lg">{t('settings.company.noCompany')}</h3>
              <p className="text-muted-foreground">{t('settings.company.noCompanyDescription')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle2 className="size-3 mr-1" />
            {t('settings.company.status.active')}
          </Badge>
        );
      case 'disabled':
        return (
          <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="size-3 mr-1" />
            {t('settings.company.status.disabled')}
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="size-3 mr-1" />
            {t('settings.company.status.pending')}
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStripeStatusBadge = () => {
    if (!company.stripeStatus?.hasAccount) {
      return (
        <Badge variant="outline" className="bg-gray-500/10">
          <XCircle className="size-3 mr-1" />
          {t('settings.company.stripe.noAccount')}
        </Badge>
      );
    }
    if (company.stripeStatus.isVerified) {
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle2 className="size-3 mr-1" />
          {t('settings.company.stripe.verified')}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
        <Clock className="size-3 mr-1" />
        {t('settings.company.stripe.pendingVerification')}
      </Badge>
    );
  };

  const subscription = company.subscribe;
  const plan = subscription?.plan;

  return (
    <div className="space-y-6">
      {/* Read-only notice */}
      <Alert>
        <AlertCircle className="size-4" />
        <AlertDescription>
          {t('settings.company.readOnlyNotice')}
        </AlertDescription>
      </Alert>

      {/* Company Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="size-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border">
                <Building2 className="size-8 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">{company.name}</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Hash className="size-3" />
                  {company.refId}
                </CardDescription>
              </div>
            </div>
            {getStatusBadge(company.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="rounded-lg bg-blue-500/10 p-2">
                <User className="size-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('settings.company.owner')}</p>
                <p className="font-medium">{company.owner}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="rounded-lg bg-purple-500/10 p-2">
                <Store className="size-4 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('settings.company.shops')}</p>
                <p className="font-medium">{company.shops?.length ?? 0} {t('settings.company.shopsCount')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="rounded-lg bg-green-500/10 p-2">
                <Users className="size-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('settings.company.employees')}</p>
                <p className="font-medium">{company.employees?.length ?? 0} {t('settings.company.employeesCount')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="rounded-lg bg-orange-500/10 p-2">
                <CreditCard className="size-4 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('settings.company.stripe.title')}</p>
                {getStripeStatusBadge()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shops List */}
      {company.shops && company.shops.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="size-5 text-purple-500" />
              {t('settings.company.shopsList')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {company.shops.map((shop, index) => (
                <Badge key={index} variant="secondary" className="px-3 py-1.5">
                  <Store className="size-3 mr-1.5" />
                  {shop}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription */}
      {subscription && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Crown className="size-5 text-yellow-500" />
                {t('settings.company.subscription.title')}
              </CardTitle>
              <Badge className="bg-gradient-to-r from-primary to-primary/70 text-primary-foreground">
                {subscription.name}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Subscription Period */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Calendar className="size-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t('settings.company.subscription.period')}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(subscription.startDate), 'dd MMM yyyy', { locale: fr })} - {format(new Date(subscription.endDate), 'dd MMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>

            {/* Plan Limits */}
            {plan && (
              <>
                <Separator />
                <div>
                  <h4 className="font-medium mb-4">{t('settings.company.subscription.limits')}</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="flex items-center gap-2">
                          <Store className="size-4 text-purple-500" />
                          {t('settings.company.subscription.maxShops')}
                        </span>
                        <span className="font-medium">{company.shops?.length ?? 0} / {plan.maxShops}</span>
                      </div>
                      <Progress value={(company.shops?.length ?? 0) / plan.maxShops * 100} className="h-2" />
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="flex items-center gap-2">
                          <Package className="size-4 text-blue-500" />
                          {t('settings.company.subscription.maxProducts')}
                        </span>
                        <span className="font-medium">- / {plan.maxProducts}</span>
                      </div>
                      <Progress value={0} className="h-2" />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Plan Features */}
                <div>
                  <h4 className="font-medium mb-4">{t('settings.company.subscription.features')}</h4>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FeatureItem
                      icon={Users}
                      label={t('settings.company.subscription.multipleEmployees')}
                      enabled={subscription.hasMultipleEmployeeAccounts}
                    />
                    <FeatureItem
                      icon={BarChart3}
                      label={t('settings.company.subscription.advancedMetrics')}
                      enabled={subscription.hasAdvancedMetrics}
                    />
                    <FeatureItem
                      icon={Scale}
                      label={t('settings.company.subscription.priceComparison')}
                      enabled={subscription.hasPriceComparison}
                    />
                    <FeatureItem
                      icon={Megaphone}
                      label={t('settings.company.subscription.adsAccess')}
                      enabled={subscription.hasAdsAccess}
                    />
                  </div>
                </div>

                {/* Free Ads */}
                {subscription.freeAdsPerYear > 0 && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
                      <Gift className="size-5 text-yellow-500" />
                      <div>
                        <p className="font-medium">{t('settings.company.subscription.freeAds')}</p>
                        <p className="text-sm text-muted-foreground">
                          {subscription.freeAdsLeft} / {subscription.freeAdsPerYear} {t('settings.company.subscription.remaining')}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Address */}
      {company.address && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="size-5 text-red-500" />
              {t('settings.company.address.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-lg bg-muted flex items-center justify-center text-2xl">
                {company.address.country?.flagEmoji ?? '🌍'}
              </div>
              <div className="flex-1">
                <p className="font-medium">
                  {company.address.address_line1}
                  {company.address.address_line2 && `, ${company.address.address_line2}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {company.address.city}
                  {company.address.postal_code && `, ${company.address.postal_code}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {company.address.country?.name}
                </p>
                {company.address.landmark && (
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="text-foreground">{t('settings.company.address.landmark')}:</span> {company.address.landmark}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stripe Status Details */}
      {company.stripeStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="size-5 text-indigo-500" />
              {t('settings.company.stripe.details')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div className="rounded-lg bg-indigo-500/10 p-2">
                  <CreditCard className="size-4 text-indigo-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('settings.company.stripe.accountStatus')}</p>
                  <p className="font-medium">{company.stripeStatus.accountStatus}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div className="rounded-lg bg-indigo-500/10 p-2">
                  {company.stripeStatus.isVerified ? (
                    <CheckCircle2 className="size-4 text-green-500" />
                  ) : (
                    <Clock className="size-4 text-yellow-500" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('settings.company.stripe.verification')}</p>
                  <p className="font-medium">
                    {company.stripeStatus.isVerified
                      ? t('settings.company.stripe.verified')
                      : t('settings.company.stripe.pendingVerification')}
                  </p>
                </div>
              </div>

              {company.stripeStatus.accountCreatedAt && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 sm:col-span-2">
                  <div className="rounded-lg bg-indigo-500/10 p-2">
                    <Calendar className="size-4 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('settings.company.stripe.createdAt')}</p>
                    <p className="font-medium">
                      {format(new Date(company.stripeStatus.accountCreatedAt), 'dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Support */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-base">{t('settings.company.needChanges')}</CardTitle>
          <CardDescription>{t('settings.company.contactSupport')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Badge variant="secondary" className="text-sm">
            {t('settings.company.supportEmail')}
          </Badge>
        </CardContent>
      </Card>
    </div>
  );
}

function FeatureItem({
  icon: Icon,
  label,
  enabled,
}: {
  icon: React.ElementType;
  label: string;
  enabled: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${enabled ? 'bg-green-500/5 border border-green-500/20' : 'bg-muted/50'}`}>
      <Icon className={`size-4 ${enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
      <span className={`text-sm ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      {enabled ? (
        <CheckCircle2 className="size-4 text-green-500 ml-auto" />
      ) : (
        <XCircle className="size-4 text-muted-foreground ml-auto" />
      )}
    </div>
  );
}
